import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import clone from 'clone';
import RoomListCard from '../cards/room-list-card.component';
import type Department from '../models/department/department';
import type DepartmentChangeData from '../models/department/department-change-data';
import type Room from '../models/room/room';
import * as styles from './modal.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import Grid from '../../../core/components/grid/grid.component';
import ConfirmationModal from './confirmation-modal.component';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly department: Department;
  readonly rooms: Room[];
  readonly handleSave: (departmentChangeData: DepartmentChangeData) => void;
  readonly onDelete: (departmentChangeData: DepartmentChangeData) => void;
  readonly nameIsEmptyOrInUse: (departmentChangeData: DepartmentChangeData) => boolean;
}

const EditDepartmentModal = ({
  showModal,
  handleClose,
  department,
  rooms,
  handleSave,
  onDelete,
  nameIsEmptyOrInUse,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [didFillData, setDidFillData] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState<Room[]>([]);
  const [updateDepartment, setUpdatedDepartment] = useState<Department>(department);
  const sortedRooms = rooms.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const resetModalState = useCallback(async () => {
    setSelectedRooms([]);
    setDidFillData(false);
  }, [setSelectedRooms, setDidFillData]);

  const handleCloseModal = useCallback(async () => {
    resetModalState();
    handleClose();
  }, [resetModalState, handleClose]);

  const initialDepartment = { ...department };

  const handleCancelModal = (): void => {
    setUpdatedDepartment(initialDepartment);
    handleCloseModal();
  };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!showModal) {
      setIsLoading(false);
    }
  }, [showModal]);

  const HandleDepartmentChange = useCallback(
    (hasFocus: boolean, value: string) => {
      if (!hasFocus) {
        const departmentCopy = { ...updateDepartment };
        departmentCopy.name = value;
        setUpdatedDepartment(departmentCopy);
      }
    },
    [setUpdatedDepartment, updateDepartment],
  );

  const handleDelete = (): void => {
    onDelete({ department, rooms: selectedRooms });
    handleCloseModal();
  };

  const handleUpdate = (): void => {
    setIsLoading(true);
    const departmentCopy = { ...clone(updateDepartment) };
    departmentCopy.name = updateDepartment.name;

    if (!nameIsEmptyOrInUse({ department: departmentCopy, rooms: selectedRooms })) {
      handleSave({ department: departmentCopy, rooms: selectedRooms });
      handleCloseModal();
      return;
    }

    updateDepartment.name = department.name;
    setSelectedRooms(rooms.filter((room) => department.rooms.some((departmentRoom) => room.id === departmentRoom.id)));

    setIsLoading(false);
  };

  const handleRoomSelected = (selected: Room): void => {
    let selectedRoomsCopy: Room[] = Object.assign([], selectedRooms);
    if (selectedRoomsCopy.some((room) => room.id === selected.id)) {
      selectedRoomsCopy = selectedRoomsCopy.filter((room) => room.id !== selected.id);
    } else {
      selectedRoomsCopy.push(selected);
    }
    setSelectedRooms(selectedRoomsCopy);
  };

  if (showModal && !stringIsNullOrWhitespace(department.name) && !didFillData) {
    setSelectedRooms(rooms.filter((room) => department.rooms.some((departmentRoom) => room.id === departmentRoom.id)));
    setDidFillData(true);
  }

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCancelModal} style={styles.modalStyle(isMobile, 800)}>
      <Column className={style(styles.modalBody(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader(isMobile, 8))}>
          {t('edit')} {updateDepartment.name}
        </span>
        <LabeledInput
          labelText={t('department-name').toString()}
          value={updateDepartment.name}
          onFocusChange={(focus, value): void => {
            HandleDepartmentChange(focus, value);
          }}
        />
        <span className={style(styles.modalText)}>{t('rooms-in-department')}</span>
        {sortedRooms.length === 0 ? (
          <span className={style(styles.emptyListMessage)}>{t('no-rooms-available-message')}</span>
        ) : (
          <Grid
            rowClass={style(styles.gridRow)}
            itemSpacing={16}
            numberOfColumns={3}
            numberOfItems={sortedRooms.length}
            renderItem={(index): React.JSX.Element => {
              const roomItem = sortedRooms[index];
              return (
                roomItem && (
                  <div
                    className={isMobile ? style(styles.rowItem(true)) : undefined}
                    key={roomItem.id}
                    onClick={(): void => {
                      handleRoomSelected(roomItem);
                    }}
                    role="presentation"
                  >
                    <RoomListCard room={roomItem} checked={selectedRooms.some((room) => room.id === roomItem.id)} />
                  </div>
                )
              );
            }}
          />
        )}
        <Row
          className={style(styles.deleteButton)}
          crossAxisAlignment={CrossAxisAlignment.Center}
          onClick={(): void => {
            setShowDeleteModal(true);
          }}
        >
          <span>{t('delete-department')}</span>
          <DeleteIcon className={style(styles.deleteIcon)} />
        </Row>
        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleUpdate} loading={isLoading}>
              {t('save')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCancelModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
      <ConfirmationModal
        showModal={showDeleteModal}
        itemName={updateDepartment.name}
        handleAccept={handleDelete}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
    </Modal>
  );
};

export default EditDepartmentModal;
